import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import DynamicReportEditableForm from "../../../../shared/dynamicEditableForm/DynamicEditableForm";
import { api } from "../../../../../utils/api";
import { Validation } from "../../../../../@types/validation.types";
import { trackPromise } from "react-promise-tracker";
import SnackbarAPICall from "../../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../../@types/snackbarAPI.types";
import { Member } from "../../../../../@types/member.types";
import JSONToExcel, { AOAtoExcel } from "../../../../../utils/commonFunctions/JSONToExcel";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { compareInvalidDates } from "../../../../../utils/commonFunctions/date";
import { Plan, PlanStatus } from "../../../../../@types/plan.types";
import { PLAN_STATUS } from "../../../../../constants/plan";
import { BENEFIT_CHANGES } from "../../../../../constants/benefitChangesConstant";
import { EMPLOYEE_INFO_CHANGES } from "../../../../../constants/employeeInfoChangesConstant";
import { useEmployerRoute } from "../../../../../hooks";
import { Group } from "../../../../../@types/group.types";
import useRouteParam from "../../../../../hooks/useRouteParam";

const chipMessage =
	"This report will get generated for a particular timestamp where it will contain the Status Changes, Personal Changes, Hire, Rehire and Termination.";

const EmployeeInfoChangesReport = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [reportFieldDetails, setReportFieldDetails] = useState<any>({
		alternative_report_name: "",
		group_number: null,
		start_date: "",
		end_date: "",
		type: ""
	});
	const { user } = useContext(AuthContext); // Extracting logged in user from central storage.
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleValidation = useCallback(async () => {
		const { group_number, start_date, end_date, type } = reportFieldDetails;

		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};
		let flag = true;

		// employee validation checking
		if (group_number === null) {
			_validation.current.reportFieldDetails["group_number"] = "Group is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (start_date.length === 0) {
			_validation.current.reportFieldDetails["start_date"] = "Start date is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		// if (end_date.length === 0) {
		// 	_validation.current.reportFieldDetails["end_date"] = "End date is required";
		// 	_validation.current["status"] = "invalid";
		// 	flag = false;
		// }
		if (start_date.length !== 0) {
			if (compareInvalidDates(start_date, end_date)) {
				_validation.current.reportFieldDetails["start_date"] = "Start Date cannot be greater than End Date!";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}
		if (end_date.length !== 0) {
			if (compareInvalidDates(start_date, end_date)) {
				_validation.current.reportFieldDetails["end_date"] = "End Date cannot be less than Start Date!";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}
		if (type.length === 0) {
			_validation.current.reportFieldDetails["type"] = "Report Type is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reportFieldDetails, validation]);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// // console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: Group) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups]
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;

			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
		},
		[reportFieldDetails]
	);

	const handleDateChange = useCallback(
		(_date: MaterialUiPickersDate, name: string) => {
			const date = new Date(_date as Date);
			const parsedDate = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: parsedDate }));
		},
		[reportFieldDetails]
	);

	const handleClearDateField = useCallback(
		(event: any, name: string) => {
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: null }));
		},
		[reportFieldDetails]
	);

	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					// console.log("Employee Info Changes Report Generation");
					const { group_number, type, start_date, end_date } = reportFieldDetails;
					const startDate = new Date(start_date);
					const endDate = new Date(end_date);

					const payload = {
						group_number,
						type,
						start_date:
							startDate.getFullYear() + "-" + (startDate.getMonth() + 1) + "-" + startDate.getDate(),
						end_date:
							end_date !== ""
								? endDate.getFullYear() + "-" + (endDate.getMonth() + 1) + "-" + endDate.getDate()
								: null
					};

					// console.log("Payload", payload);
					const response: Member[] = await trackPromise(api.reports.employeeInfoChangesReport(payload));
					// console.log("Response", response);
					if (response) {
						// console.log("Response", response);

						const fileName = reportFieldDetails.alternative_report_name
							? reportFieldDetails.alternative_report_name
							: "Employee Info Changes Report";

						const excelPayload = {
							user: {
								role: user?.role,
								name: user?.first_name + " " + user?.last_name
							},
							reportFieldDetails,
							response
						};

						if (response.length !== 0) {
							await trackPromise(AOAtoExcel(excelPayload, fileName));
						} else {
							await trackPromise(JSONToExcel(excelPayload, fileName));
						}

						setTimeout(() => {
							setSnackbarAPICallProps(
								Object.assign({}, snackbarAPICallProps, {
									open: true,
									message: "Employee Info Changes Report Fetched Successfully and File Downloaded!",
									severity: "success"
								})
							);
						}, 2000);
					}
				}
			} catch (error) {
				// console.log("error", error);
				alert("Employee Info Changes Report Generation Unsuccessful!");
				throw error;
			}
		},
		[handleValidation, reportFieldDetails, snackbarAPICallProps, user]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.employee_info_changes_report);
		getAssignGroups();
	}, [getAssignGroups, setDashboardHeader]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber, handleSelectGroup]);

	useEffect(() => {
		if (reportFieldDetails.start_date === null)
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { start_date: "" }));
		if (reportFieldDetails.end_date === null)
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { end_date: "" }));
	}, [reportFieldDetails]);

	useEffect(() => {
		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					"Employee Info Changes Report Generation": [
						{
							label: "Group",
							name: "group_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								isEmployerRoute && queryGroupNumber
									? selectedGroup?.name
									: reportFieldDetails.group_number,
							type: isEmployerRoute && queryGroupNumber ? "textfield" : "select",
							options: [...assignedGroups]
						},
						{
							label: "Type",
							name: "type",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value: reportFieldDetails.type,
							type: "select",
							options: EMPLOYEE_INFO_CHANGES
						},
						{
							label: "Start Date",
							name: "start_date",
							onChange: (date: MaterialUiPickersDate, name: string) => handleDateChange(date, name),
							placeholder: "Enter start date",
							value: reportFieldDetails.start_date,
							type: "date"
						},
						{
							label: "End Date",
							name: "end_date",
							onChange: (date: MaterialUiPickersDate, name: string) => handleDateChange(date, name),
							placeholder: "Enter end date",
							value: reportFieldDetails.end_date,
							type: "date"
						},
						{
							label: "Alternative Report Name",
							name: "alternative_report_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "Enter alternative report name",
							value: reportFieldDetails.alternative_report_name,
							type: "textfield"
						}
					]
				}
			)
		);
	}, [
		assignedGroups,
		reportFieldDetails,
		handleChange,
		handleDateChange,
		isEmployerRoute,
		queryGroupNumber,
		selectedGroup?.name
	]);

	// console.log("reportFieldDetails", reportFieldDetails);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={chipMessage} variant="outlined" />
			</div>
			<DynamicReportEditableForm
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				// selectedPlanName={selectedPlan?.plan_name}
				handleClearDateField={handleClearDateField}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default EmployeeInfoChangesReport;
